import { FC, ReactNode, useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  createTrustIdApplication,
  getLatestTrustIdApplicationForAdi,
  getTrustIdApplication,
} from "../../../../core/services/right-to-work-service";
import { TrustIdApplication } from "../../../../core/models/right-to-work-model";

const POLL_INTERVAL_MS = 15000;
const TERMINAL_STATUSES = ["completed", "failed"];

const TrustIdFooter = () => (
  <>
    <span className="text-xs font-mono uppercase tracking-widest text-gray-500">
      Checks provided by TrustID
    </span>{" "}
    <a
      href="mailto:compliance@capsil.co.uk"
      className="text-xs sm:text-sm text-gray-400 underline hover:text-gray-200"
    >
      compliance@capsil.co.uk
    </a>
  </>
);

const NextCheckNotice = ({ seconds }: { seconds: number }) => (
  <p className="mt-5 text-xs text-gray-500">
    {`We'll automatically recheck your status in ${seconds}s.`}
  </p>
);

interface BannerShellProps {
  accentClassName: string;
  badgeClassName: string;
  badgeLabel: string;
  dueDate?: string;
  heading: string;
  footer?: ReactNode;
  children: ReactNode;
}

const BannerShell: FC<BannerShellProps> = ({
  accentClassName,
  badgeClassName,
  badgeLabel,
  dueDate,
  heading,
  footer = <TrustIdFooter />,
  children,
}) => (
  <div className="relative mb-4 overflow-hidden rounded-xl bg-[#0C0D10]">
    <span
      className={`absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b ${accentClassName}`}
    />

    <div className="flex flex-col md:flex-row">
      <div className="min-w-0 flex-1 py-6 pl-8 pr-6 sm:py-8 sm:pl-10 sm:pr-8">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex items-center gap-2 text-xs font-mono font-semibold uppercase tracking-widest ${badgeClassName}`}
          >
            <span className={`h-2 w-2 rounded-full bg-current`} />
            {badgeLabel}
          </span>
          {dueDate && (
            <span className="rounded border border-white/15 px-3 py-1 text-xs font-mono uppercase tracking-widest text-gray-300">
              Due by {dueDate}
            </span>
          )}
        </div>

        <h2 className="mt-4 text-2xl font-bold text-white sm:text-3xl">
          {heading}
        </h2>

        {children}

        <div className="mt-6 border-t border-white/10 pt-4">{footer}</div>
      </div>
    </div>
  </div>
);

export interface RightToWorkBannerProps {
  applicantEmail?: string;
  applicantName?: string;
  applicantAdiNumber?: string;
  dueDate?: string;
}

const RightToWorkBanner: FC<RightToWorkBannerProps> = ({
  applicantEmail,
  applicantName,
  applicantAdiNumber,
  dueDate,
}) => {
  const [application, setApplication] = useState<TrustIdApplication | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [hasCheckedInitialStatus, setHasCheckedInitialStatus] =
    useState<boolean>(false);
  const [secondsUntilNextCheck, setSecondsUntilNextCheck] = useState<number>(
    POLL_INTERVAL_MS / 1000,
  );

  useEffect(() => {
    if (!applicantAdiNumber) {
      return;
    }
    let cancelled = false;
    getLatestTrustIdApplicationForAdi(applicantAdiNumber)
      .then((res) => {
        if (!cancelled && res.data) {
          setApplication(res.data);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setHasCheckedInitialStatus(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [applicantAdiNumber]);

  useEffect(() => {
    if (!application || TERMINAL_STATUSES.includes(application.status)) {
      return;
    }
    setSecondsUntilNextCheck(POLL_INTERVAL_MS / 1000);
    const tickId = setInterval(() => {
      setSecondsUntilNextCheck((prev) => {
        if (prev <= 1) {
          getTrustIdApplication(application.id)
            .then((res) => setApplication(res.data))
            .catch(() => {});
          return POLL_INTERVAL_MS / 1000;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tickId);
    // Deliberately keyed on id/status only, not the whole `application`
    // object, so a poll response updating other fields doesn't restart the
    // interval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [application?.id, application?.status]);

  async function handleStartCheck() {
    if (
      !applicantEmail ||
      !applicantName ||
      !applicantAdiNumber ||
      isSubmitting
    ) {
      return;
    }
    setIsSubmitting(true);
    try {
      const res = await createTrustIdApplication({
        email: applicantEmail,
        name: applicantName,
        adi_number: applicantAdiNumber,
        branch: "adin",
      });
      setApplication(res.data);
      if (res.data.guest_link_url) {
        // Fires within the click handler's async continuation; some
        // browsers (Safari in particular) may block this once the click's
        // user-activation window has expired, hence "Continue check" below
        // stays as a manual fallback.
        window.open(res.data.guest_link_url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      toast.error(
        "Failed to start your right to work check. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleContinueCheck() {
    if (application?.guest_link_url) {
      window.open(application.guest_link_url, "_blank", "noopener,noreferrer");
    }
  }

  if (!hasCheckedInitialStatus && applicantAdiNumber) {
    return null;
  }

  const status = application?.status ?? "not_started";

  if (status === "not_started" || status === "pending") {
    return (
      <>
        <Toaster />
        <BannerShell
          accentClassName="from-orange-500 to-red-600"
          badgeClassName="text-orange-500"
          badgeLabel="Not started"
          dueDate={dueDate}
          heading="You must complete your right to work check"
          footer={
            <>
              <span className="text-xs font-mono uppercase tracking-widest text-gray-500">
                TrustID is a Government approved Idenity vendor.
              </span>{" "}
              <a
                href="https://www.trustid.co.uk"
                className="text-xs sm:text-sm text-gray-400 underline hover:text-gray-200"
              >
                https://www.trustid.co.uk
              </a>
              {/* <a
                href="mailto:compliance@capsil.co.uk"
                className="text-xs sm:text-sm text-gray-400 underline hover:text-gray-200"
              >
                compliance@capsil.co.uk
              </a> */}
            </>
          }
        >
          <p className="mt-3 max-w-2xl text-sm sm:text-base text-gray-400">
            UK law requires us to verify your right to work before you can
            accept new jobs.
          </p>

          <p className="mt-2 text-sm sm:text-base font-semibold text-orange-500">
            From November 1st you <strong>will not</strong> be able to accept
            new jobs until your Verification is approved.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <button
              type="button"
              onClick={handleStartCheck}
              disabled={!applicantEmail || !applicantName || isSubmitting}
              className="rounded bg-white px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? "Starting..." : "Start check"}
            </button>
            <span className="text-xs sm:text-sm text-gray-500">
              Takes about 3 minutes · Bring your passport or BRP
            </span>
          </div>
        </BannerShell>
      </>
    );
  }

  if (status === "failed") {
    return (
      <BannerShell
        accentClassName="from-red-500 to-red-700"
        badgeClassName="text-red-400"
        badgeLabel="Check failed"
        dueDate={dueDate}
        heading="Your right to work check was rejected"
      >
        <p className="mt-3 max-w-2xl text-sm text-gray-400 sm:text-base">
          We could not verify your right to work application, a member of our
          team will be in contact with you to assist further.
        </p>

        {application?.error_message && (
          <p className="mt-4 rounded border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-100">
            {application.error_message}
          </p>
        )}

        <p className="mt-3 max-w-2xl text-sm text-gray-400 sm:text-base">
          If you would not like to wait for our team to contact you, you can
          contact us directly on <strong>0333 1886 627</strong>.
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-4">
          <a href="tel:03331886627">
            <button
              type="button"
              className="rounded bg-white px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Call Us
            </button>
          </a>
          {/* <button
            type="button"
            onClick={handleStartCheck}
            disabled={isSubmitting || !applicantEmail || !applicantName}
            className="rounded bg-white px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? "Starting..." : "Resubmit"}
          </button> */}
          <span className="text-xs text-gray-500 sm:text-sm">
            Please have your passport or identity document to hand.
          </span>
        </div>
      </BannerShell>
    );
  }

  if (status === "review") {
    return (
      <BannerShell
        accentClassName="from-[#8FB4E6] to-[#8FB4E6]"
        badgeClassName="text-[#8FB4E6]"
        badgeLabel="In review"
        dueDate={dueDate}
        heading="Your right to work check is being reviewed"
      >
        <p className="mt-3 max-w-2xl text-sm text-gray-400 sm:text-base">
          Your documents have been submitted to TrustID and are now awaiting
          verification. You do not need to take any further action right now.
        </p>

        <p className="mt-2 text-sm font-semibold text-[#8FB4E6] sm:text-base">
          We&apos;ll update your status automatically as soon as the review is
          complete.
        </p>

        <NextCheckNotice seconds={secondsUntilNextCheck} />
      </BannerShell>
    );
  }

  if (status === "completed") {
    return (
      <BannerShell
        accentClassName="from-emerald-500 to-emerald-700"
        badgeClassName="text-emerald-400"
        badgeLabel="Verified"
        heading="Your right to work check is complete"
      >
        <p className="mt-3 max-w-2xl text-sm text-gray-400 sm:text-base">
          Your right to work has been successfully verified and you will be able
          to accept jobs as normal from November 1st. You do not need to take
          any further action.
        </p>
      </BannerShell>
    );
  }

  return (
    <BannerShell
      accentClassName="from-orange-500 to-red-600"
      badgeClassName="text-orange-500"
      badgeLabel="In progress"
      dueDate={dueDate}
      heading="Finish your right to work check"
    >
      <p className="mt-3 max-w-2xl text-sm sm:text-base text-gray-400">
        UK law requires us to verify your right to work before you can accept
        new jobs.
      </p>

      <p className="mt-2 text-sm sm:text-base font-semibold text-orange-500">
        From November 1st you <strong>will not</strong> be able to accept new
        jobs until your Verification is approved.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button
          type="button"
          onClick={handleContinueCheck}
          className="rounded bg-white px-6 py-3 text-sm font-semibold text-black transition-colors hover:bg-gray-200"
        >
          Continue check
        </button>
        <span className="text-xs sm:text-sm text-gray-500">
          About 2 minutes left · Bring your passport or BRP
        </span>
      </div>

      <NextCheckNotice seconds={secondsUntilNextCheck} />
    </BannerShell>
  );
};

export { RightToWorkBanner };
